import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Checkbox, CHECKBOX_DEFAULT_SIZE } from '../checkbox/checkbox.component';
import { DropdownToggleHelper } from '../../helpers/dropdown-toggle.helper';

export interface CheckboxMultiDropdownOption {
  id: string;
  name: string;
}

/** Optional single action rendered at the bottom of the open menu (e.g. Clear). */
export interface CheckboxMultiDropdownFooterAction {
  label: string;
  disabled?: boolean;
}

export type CheckboxMultiDropdownAppearance = 'default' | 'input';

/** Sentinel for the "All" row when handling keyboard toggle. */
export const CMD_ALL_OPTION_ID = '__all__';

/**
 * Multi-select filter dropdown (Appointments Clear/Cancel/Apply pattern).
 * Empty selection means "All". `appearance="input"` matches Figma form input.
 * Keyboard: Enter/Space/ArrowDown open; Escape closes; arrows move options; Space toggles.
 * Set `fixedOverlay` to append the menu to `document.body` (modals / overflow parents).
 * Project an element with the `cmdTriggerIcon` attribute to show it before the trigger label.
 */
@Component({
  selector: 'app-checkbox-multi-dropdown',
  standalone: true,
  imports: [CommonModule, Checkbox],
  templateUrl: './checkbox-multi-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './checkbox-multi-dropdown.component.scss',
})
export class CheckboxMultiDropdownComponent implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private static nextId = 0;
  readonly instanceId = `cmd-${CheckboxMultiDropdownComponent.nextId++}`;
  readonly listboxId = `${this.instanceId}-listbox`;
  readonly valueLabelId = `${this.instanceId}-value`;
  readonly allOptionId = CMD_ALL_OPTION_ID;

  @ViewChild('triggerBtn') triggerBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('menuPanel') menuPanel?: ElementRef<HTMLElement>;

  @Input() options: CheckboxMultiDropdownOption[] = [];
  @Input()
  set selectedIds(value: string[]) {
    this._selectedIds = value ?? [];
    // Instant-apply menus stay open after parent clears via footerAction; keep draft in sync.
    if (this.isOpen && !this.showFooter) {
      this.syncDraftFromSelected();
    }
  }
  get selectedIds(): string[] {
    return this._selectedIds;
  }
  private _selectedIds: string[] = [];
  @Input() placeholder = 'All';
  @Input() allOptionLabel = 'All';
  @Input() multipleSelectionText = '{{count}} selected';
  @Input() appearance: CheckboxMultiDropdownAppearance = 'default';
  /** Passed through to `app-checkbox` (px). Default 16; Create Discount uses 22. */
  @Input() checkboxSize = CHECKBOX_DEFAULT_SIZE;
  /** Id of a visible label element (e.g. Location). */
  @Input() ariaLabelledBy: string | null = null;
  /** Field name used in the accessible name (e.g. "Location"). */
  @Input() ariaLabel: string | null = null;
  /**
   * When false, hides Clear/Cancel/Apply and applies each toggle immediately
   * (Create Discount popup). Default true preserves filter-modal behavior.
   */
  @Input() showFooter = true;
  /**
   * When true, positions the menu with `position: fixed` and appends it to
   * `document.body` so modal overflow/stacking contexts cannot clip it.
   */
  @Input() fixedOverlay = false;
  /**
   * When false, hides the leading "All" row and treats an empty selection as
   * empty (not "all"). Used by Day of Week in Create Discount.
   */
  @Input() showAllOption = true;
  /**
   * When true with `showAllOption`: selecting "All" emits every option id, and
   * an empty `selectedIds` means none selected (form Select All). Default false
   * keeps filter semantics where empty means all.
   */
  @Input() selectAllEmitsIds = false;
  /**
   * When true, shows a small spinner next to the chevron and blocks interaction.
   * Used while Wash Package options load for the selected Wash Type.
   */
  @Input()
  set loading(value: boolean) {
    this._loading = !!value;
    if (this._loading && this.isOpen) {
      this.closeWithoutApply(false);
    }
  }
  get loading(): boolean {
    return this._loading;
  }
  private _loading = false;
  /** Shown inside the open menu when `options` is empty (and not loading). */
  @Input() emptyMessage = 'No options available';
  /**
   * Optional single footer action (e.g. Clear) below the option list.
   * Independent of `showFooter` (Clear/Cancel/Apply). Omit to hide.
   */
  @Input() footerAction: CheckboxMultiDropdownFooterAction | null = null;

  /** When true, trigger is non-interactive (e.g. no employees for location). */
  @Input()
  set disabled(value: boolean) {
    this._disabled = !!value;
    if (this._disabled && this.isOpen) {
      this.closeWithoutApply(false);
    }
  }
  get disabled(): boolean {
    return this._disabled || this._loading;
  }
  private _disabled = false;

  @Output() selectedIdsChange = new EventEmitter<string[]>();
  /** Emitted when the optional `footerAction` is clicked; parent decides next steps. */
  @Output() footerActionClick = new EventEmitter<void>();
  /** Emits when the menu opens or closes so parents can keep sibling popovers exclusive. */
  @Output() openedChange = new EventEmitter<boolean>();

  isOpen = false;
  /** True after fixed-overlay top/left have been applied (prevents top-left flash). */
  fixedOverlayReady = false;
  private draftIds = new Set<string>();
  /** Tracks Select All while the menu is open (filter empty=all, or form all-ids). */
  private isAllMode = false;
  private overlayScrollParents: EventTarget[] = [];
  private readonly onOverlayReposition = (): void => this.positionMenu();
  private menuHomeParent: HTMLElement | null = null;
  private menuHomeNextSibling: ChildNode | null = null;
  /**
   * When true, the next open keeps `draftIds` instead of reseeding from `selectedIds`.
   * Set by programmatic `close()` (e.g. another filter opened); cleared on Apply / discard.
   */
  private retainDraftOnNextOpen = false;

  get triggerLabel(): string {
    if (this.showAllOption && !this.selectAllEmitsIds) {
      if ((this.isOpen && this.isAllMode) || (!this.isOpen && this.selectedIds.length === 0)) {
        return this.placeholder;
      }
    }

    const ids = this.isOpen ? this.draftSelectionIds : this.selectedIds;
    if (ids.length === 0) {
      return this.placeholder;
    }
    if (
      this.showAllOption &&
      this.selectAllEmitsIds &&
      this.options.length > 0 &&
      ids.length === this.options.length
    ) {
      return this.allOptionLabel;
    }
    if (ids.length === 1) {
      return this.options.find(opt => opt.id === ids[0])?.name ?? this.placeholder;
    }
    return this.multipleSelectionText.replace('{{count}}', String(ids.length));
  }

  get isAllSelected(): boolean {
    if (!this.showAllOption) {
      return this.options.length > 0 && this.draftIds.size === this.options.length;
    }
    if (this.options.length === 0) {
      // Filter mode: empty options still count as "all". Select-all-ids cannot.
      return !this.selectAllEmitsIds;
    }
    return this.isAllMode || this.draftIds.size === this.options.length;
  }

  /** IDs represented in the open menu (all option ids when in all-mode). */
  private get draftSelectionIds(): string[] {
    if (this.isAllMode) {
      return this.options.map(option => option.id);
    }
    return Array.from(this.draftIds);
  }

  /** Prefer label+value ids so SR/devtools show e.g. "Location All Locations". */
  get computedLabelledBy(): string {
    return [this.ariaLabelledBy, this.valueLabelId].filter(Boolean).join(' ');
  }

  /** Always-available name fallback for tools that ignore labelledby. */
  get accessibleName(): string {
    const field = (this.ariaLabel || '').trim();
    const value = this.triggerLabel;
    if (field && value) {
      return `${field}: ${value}`;
    }
    return field || value || 'Multi-select';
  }

  toggleOpen(event?: Event): void {
    event?.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.closeWithoutApply(false);
      return;
    }
    this.openMenu();
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.isOpen) {
          this.openMenu(event.key === 'ArrowUp' ? 'last' : 'first');
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.closeWithoutApply(true);
        }
        break;
      default:
        break;
    }
  }

  onListKeydown(event: KeyboardEvent): void {
    const options = this.optionButtons();
    if (!options.length) {
      return;
    }
    const current = document.activeElement as HTMLElement | null;
    const index = options.findIndex(el => el === current);

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next = index < 0 ? 0 : Math.min(index + 1, options.length - 1);
        options[next]?.focus();
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prev = index < 0 ? options.length - 1 : Math.max(index - 1, 0);
        options[prev]?.focus();
        break;
      }
      case 'Home':
        event.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeWithoutApply(true);
        break;
      case ' ':
      case 'Enter': {
        event.preventDefault();
        const optionId = current?.getAttribute('data-option-id');
        if (!optionId) {
          break;
        }
        if (optionId === CMD_ALL_OPTION_ID) {
          this.onToggleAll();
        } else {
          this.onToggleOne(optionId);
        }
        break;
      }
      default:
        break;
    }
  }

  isDraftSelected(id: string): boolean {
    return this.isAllSelected || this.draftIds.has(id);
  }

  onToggleAll(): void {
    if (!this.showAllOption) {
      return;
    }
    if (this.isAllSelected) {
      this.isAllMode = false;
      this.draftIds.clear();
      this.commitDraftIfInstant();
      return;
    }
    this.isAllMode = true;
    this.draftIds.clear();
    this.commitDraftIfInstant();
  }

  onToggleOne(id: string): void {
    if (this.showAllOption && this.isAllMode) {
      this.isAllMode = false;
      this.draftIds = new Set(
        this.options.map(option => option.id).filter(optionId => optionId !== id)
      );
      this.commitDraftIfInstant();
      return;
    }

    if (this.draftIds.has(id)) {
      this.draftIds.delete(id);
      this.commitDraftIfInstant();
      return;
    }

    this.draftIds.add(id);
    if (this.showAllOption && this.draftIds.size === this.options.length) {
      this.isAllMode = true;
      this.draftIds.clear();
    }
    this.commitDraftIfInstant();
  }

  onClear(): void {
    // Filter "All" mode clears back to all; select-all-ids clears to none.
    this.isAllMode = this.showAllOption && !this.selectAllEmitsIds;
    this.draftIds.clear();
  }

  onFooterActionClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.footerAction?.disabled) {
      return;
    }
    this.footerActionClick.emit();
  }

  onCancel(): void {
    this.closeWithoutApply(true);
  }

  onApply(): void {
    this.retainDraftOnNextOpen = false;
    this.selectedIdsChange.emit(this.toAppliedSelection());
    this.teardownOverlay();
    DropdownToggleHelper.close(this.instanceId);
    this.setOpen(false);
    this.focusTrigger();
  }

  /**
   * Closes the menu without applying. Preserves unsubmitted draft selections so a
   * sibling filter can open exclusively without discarding the user's checks.
   * Safe to call when already closed.
   */
  close(restoreFocus = false): void {
    if (!this.isOpen) {
      return;
    }
    this.retainDraftOnNextOpen = true;
    this.teardownOverlay();
    DropdownToggleHelper.close(this.instanceId);
    this.setOpen(false);
    if (restoreFocus) {
      this.focusTrigger();
    }
  }

  trackById(_index: number, option: CheckboxMultiDropdownOption): string {
    return option.id;
  }

  ngOnDestroy(): void {
    this.closeWithoutApply(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as Node | null;
    if (!target) {
      return;
    }
    const panel = this.menuPanel?.nativeElement;
    if (this.host.nativeElement.contains(target) || panel?.contains(target)) {
      return;
    }
    this.closeWithoutApply(false);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen || event.key !== 'Escape') {
      return;
    }
    const target = event.target as Node | null;
    const panel = this.menuPanel?.nativeElement;
    if (target && !this.host.nativeElement.contains(target) && !(panel && panel.contains(target))) {
      return;
    }
    event.preventDefault();
    this.closeWithoutApply(true);
  }

  private openMenu(focus: 'first' | 'last' = 'first'): void {
    if (this.retainDraftOnNextOpen) {
      this.retainDraftOnNextOpen = false;
    } else {
      this.syncDraftFromSelected();
    }
    this.fixedOverlayReady = false;
    DropdownToggleHelper.open(
      this.instanceId,
      () => this.closeWithoutApply(false),
      this.host.nativeElement
    );
    this.setOpen(true);
    // Sync open/hidden bindings before measuring + body-portal (same as date picker).
    this.cdr.detectChanges();
    if (this.fixedOverlay && typeof window !== 'undefined') {
      this.positionMenu();
      this.bindOverlayRepositionListeners();
    }
    queueMicrotask(() => {
      const options = this.optionButtons();
      if (!options.length) {
        return;
      }
      (focus === 'last' ? options[options.length - 1] : options[0])?.focus();
    });
  }

  private closeWithoutApply(restoreFocus: boolean): void {
    if (!this.isOpen) {
      return;
    }
    this.retainDraftOnNextOpen = false;
    if (this.showFooter) {
      this.syncDraftFromSelected();
    }
    this.teardownOverlay();
    DropdownToggleHelper.close(this.instanceId);
    this.setOpen(false);
    if (restoreFocus) {
      this.focusTrigger();
    }
  }

  private teardownOverlay(): void {
    this.unbindOverlayRepositionListeners();
    this.restoreMenuHome();
    this.clearFixedMenuStyles();
    this.fixedOverlayReady = false;
  }

  private positionMenu(): void {
    setTimeout(() => {
      if (!this.isOpen) {
        return;
      }
      this.applyMenuPosition();
      requestAnimationFrame(() => this.applyMenuPosition());
    }, 0);
  }

  private applyMenuPosition(): void {
    if (!this.isOpen) {
      return;
    }
    const menu =
      this.menuPanel?.nativeElement ??
      (this.host.nativeElement.querySelector('.cmd-menu') as HTMLElement | null);
    const trigger = this.triggerBtn?.nativeElement;
    if (!menu || !trigger || !this.fixedOverlay) {
      return;
    }

    this.attachMenuToBody(menu);

    const rect = trigger.getBoundingClientRect();
    const menuWidth = Math.max(menu.offsetWidth || rect.width, rect.width);
    const menuHeight = menu.offsetHeight || 240;
    const gap = 4;
    const viewportPadding = 8;

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = Math.max(viewportPadding, rect.top - menuHeight - gap);
    }

    let left = rect.left;
    left = Math.min(
      Math.max(viewportPadding, left),
      window.innerWidth - menuWidth - viewportPadding
    );

    menu.style.position = 'fixed';
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.right = 'auto';
    menu.style.width = `${rect.width}px`;
    menu.style.minWidth = `${rect.width}px`;
    menu.style.zIndex = '10050';
    menu.style.transform = 'none';
    // Inline visibility so the panel shows even before the next CD cycle applies
    // `.cmd-menu--is-positioned` (modals / Eager CD).
    menu.style.visibility = 'visible';
    menu.style.pointerEvents = 'auto';
    this.fixedOverlayReady = true;
    this.cdr.detectChanges();
    // CD can re-insert the panel under a transformed/overflow parent — keep it on body.
    this.attachMenuToBody(menu);
  }

  private attachMenuToBody(menu: HTMLElement): void {
    if (typeof document === 'undefined' || menu.parentElement === document.body) {
      DropdownToggleHelper.setActivePanel(this.instanceId, menu);
      return;
    }
    this.menuHomeParent = menu.parentElement;
    this.menuHomeNextSibling = menu.nextSibling;
    document.body.appendChild(menu);
    DropdownToggleHelper.setActivePanel(this.instanceId, menu);
  }

  private restoreMenuHome(): void {
    const menu = this.menuPanel?.nativeElement;
    if (!menu || !this.menuHomeParent) {
      this.menuHomeParent = null;
      this.menuHomeNextSibling = null;
      return;
    }
    if (menu.parentElement === document.body) {
      this.menuHomeParent.insertBefore(menu, this.menuHomeNextSibling);
    }
    this.menuHomeParent = null;
    this.menuHomeNextSibling = null;
  }

  private clearFixedMenuStyles(): void {
    const menu =
      this.menuPanel?.nativeElement ??
      (this.host.nativeElement.querySelector('.cmd-menu') as HTMLElement | null);
    if (!menu || !this.fixedOverlay) {
      return;
    }
    menu.style.position = '';
    menu.style.top = '';
    menu.style.left = '';
    menu.style.right = '';
    menu.style.width = '';
    menu.style.minWidth = '';
    menu.style.zIndex = '';
    menu.style.transform = '';
    menu.style.visibility = '';
    menu.style.pointerEvents = '';
  }

  private bindOverlayRepositionListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.unbindOverlayRepositionListeners();
    window.addEventListener('resize', this.onOverlayReposition);
    const scrollParents = this.collectScrollParents(this.host.nativeElement);
    this.overlayScrollParents = scrollParents;
    for (const parent of scrollParents) {
      parent.addEventListener('scroll', this.onOverlayReposition, true);
    }
  }

  private unbindOverlayRepositionListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('resize', this.onOverlayReposition);
    for (const parent of this.overlayScrollParents) {
      parent.removeEventListener('scroll', this.onOverlayReposition, true);
    }
    this.overlayScrollParents = [];
  }

  private collectScrollParents(element: HTMLElement): EventTarget[] {
    const parents: EventTarget[] = [window];
    let node: HTMLElement | null = element.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      const overflowValues = `${style.overflow}|${style.overflowY}|${style.overflowX}`;
      if (/(auto|scroll|overlay)/.test(overflowValues)) {
        parents.push(node);
      }
      node = node.parentElement;
    }
    return parents;
  }

  /** Instant-apply mode: emit on each toggle; menu stays open until closed elsewhere. */
  private commitDraftIfInstant(): void {
    if (this.showFooter || !this.isOpen) {
      return;
    }
    this.selectedIdsChange.emit(this.toAppliedSelection());
  }

  private setOpen(open: boolean): void {
    if (this.isOpen === open) {
      return;
    }
    this.isOpen = open;
    this.openedChange.emit(open);
  }

  private focusTrigger(): void {
    queueMicrotask(() => this.triggerBtn?.nativeElement.focus());
  }

  private optionButtons(): HTMLElement[] {
    const root = this.menuPanel?.nativeElement ?? this.host.nativeElement;
    return Array.from(root.querySelectorAll('.cmd-item')) as HTMLElement[];
  }

  private syncDraftFromSelected(): void {
    const optionIds = this.options.map(option => option.id);
    const selected = new Set(this.selectedIds);

    if (!this.showAllOption) {
      this.isAllMode = false;
      this.draftIds = selected;
      return;
    }

    if (optionIds.length === 0) {
      this.isAllMode = !this.selectAllEmitsIds;
      this.draftIds.clear();
      return;
    }

    const allExplicitlySelected =
      selected.size === optionIds.length && optionIds.every(id => selected.has(id));

    if (this.selectAllEmitsIds) {
      // Empty = none; every id selected = All wash checked.
      if (selected.size === 0) {
        this.isAllMode = false;
        this.draftIds.clear();
        return;
      }
      if (allExplicitlySelected) {
        this.isAllMode = true;
        this.draftIds.clear();
        return;
      }
      this.isAllMode = false;
      this.draftIds = selected;
      return;
    }

    if (this.selectedIds.length === 0 || allExplicitlySelected) {
      this.isAllMode = true;
      this.draftIds.clear();
      return;
    }

    this.isAllMode = false;
    this.draftIds = selected;
  }

  /**
   * Filter mode (`showAllOption`, default): empty array means "all".
   * Select-all-ids mode: "All" emits every option id; empty is a real empty selection.
   */
  private toAppliedSelection(): string[] {
    if (!this.showAllOption) {
      return [...this.draftIds];
    }
    if (this.isAllMode || this.draftIds.size === this.options.length) {
      return this.selectAllEmitsIds ? this.options.map(option => option.id) : [];
    }
    return [...this.draftIds];
  }
}
